import type { DocumentMeta, Point } from './types';

/**
 * A guide is a single line across the whole canvas, so it needs nothing but its
 * axis and its position along the other one, in document pixels.
 */
export interface Guide {
  id: string;
  axis: 'horizontal' | 'vertical';
  position: number;
}

let counter = 0;

export function createGuide(axis: Guide['axis'], position: number): Guide {
  counter += 1;
  return { id: `guide-${counter}-${Date.now().toString(36)}`, axis, position };
}

/**
 * Guide under the pointer, if any. `tolerance` is in document pixels, so callers
 * scale it by the zoom to keep the grab area the same size on screen.
 */
export function guideAt(
  guides: readonly Guide[],
  point: Point,
  tolerance: number,
): Guide | null {
  let best: Guide | null = null;
  let bestDistance = tolerance;
  for (const guide of guides) {
    const away = Math.abs(guide.axis === 'horizontal' ? point.y - guide.position : point.x - guide.position);
    if (away <= bestDistance) {
      best = guide;
      bestDistance = away;
    }
  }
  return best;
}

/** Guides that fall outside the canvas are dropped, as Photoshop drops them. */
export function guidesInsideCanvas(guides: readonly Guide[], doc: DocumentMeta): Guide[] {
  return guides.filter((guide) =>
    guide.axis === 'horizontal'
      ? guide.position >= 0 && guide.position <= doc.height
      : guide.position >= 0 && guide.position <= doc.width,
  );
}
